import {booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, input, numberAttribute, ViewContainerRef} from "@angular/core";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {DomSanitizer} from "@angular/platform-browser";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";
import {nil} from "felles-ng2/typer/nullish";
import {distinctUntilChanged, filter, fromEvent, merge, mergeMap, Observable, of, switchMap, tap} from "rxjs";
import {Overlay} from "@angular/cdk/overlay";
import {Lenkeinformasjon} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/lenke-rediger/lenke-rediger";
import {toObservable} from "@angular/core/rxjs-interop";
import {delay, map} from "rxjs/operators";
import {leverForsinketMedMindreAnnenHendelse} from "felles-ng2/støttefunksjoner/observable";
import {Lenkepanel} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/lenke-rediger/Lenkepanel";
import {HttpsUrl, InternlenkeMålEntitetType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erInternLenke, InternlenkeAttributter, lesIdFraInternlenke, lesTypeFraInternlenke, lesUuidFraInternlenke} from "felles-ng2/modell/lenkerelasjonModellHjelper";
import {caseForEnumManglerIkkeKastException} from "felles-ng2/støttefunksjoner/enumer";

const debug = initDebugger('HtmlFormatertKomponent');

/**
 * Viser html-formatert innhold, tilpasset web.
 *
 * Merk: i ajs-koden finnes en for web og en for mobil, med felles kode i en abstrakt superklasse.
 * Superklasser kan ikke inneholder @annotasjoner - derfor er det lite igjen til en felles basisklasse, derfor er denne droppet her.
 */
@Component({
    selector: 'html-formatert',
    template: '',
    styleUrl: 'html-formatert.scss',
    host: {
        '[class.trunkert]': 'trunker() > 0',
        '[style.--trunker-antall-linjer]': 'trunker()',
        '[innerHTML]': 'sikkertInnhold()'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HtmlFormatertKomponent {
    static ajsSelector = 'htmlFormatert';

    innhold = input.required<string | nil>();
    /** Trunkerer teksten til angitt antall linjer dersom antall > 0 */
    trunker = input(0, {transform: numberAttribute});
    /**
     * Lar Angular sanitere innholdet i stedet for å vise det som det er.
     *
     * Innhold som er lagret på server er allerede sanitert der (se
     * no.systemfabrikken.lederplan.infrastruktur.datatyper.Strenger.HtmlTekst), og vises derfor
     * uendret som standard. Innhold som ikke har vært innom den saniteringen – feks html generert av
     * KI på klienten – må saniteres her.
     */
    saniterInnhold = input(false, {transform: booleanAttribute});

    sikkertInnhold = computed(() => this.saniterInnhold() ?
      this.innhold() ?? '' :
      this.sanitizer.bypassSecurityTrustHtml(this.innhold() ?? ''));
    private lenker = this.lenkerIInnhold();
    private lenkepanel = new Lenkepanel(this.overlay, this.viewContainerRef);

    constructor(private el: ElementRef<HTMLElement>,
                private navigasjon: Navigasjon,
                private sanitizer: DomSanitizer,
                private overlay: Overlay,
                private viewContainerRef: ViewContainerRef) {
        this.visLenkepanelVedAktivLenke();
        //vet ikke helt hva vi vinner på å håndtere lenkene selv? eneste forskjell i vår egent håndtering her
        //er at vi ved internlenker ikke åpner et nytt vindu..
        this.håndterKlikkPåLenke();
    }

    //private hjelpemetoder

    //håndtering av lenker

    private lenkerIInnhold(): Observable<HTMLAnchorElement[]> {
        return toObservable(this.sikkertInnhold)
          .pipe(
            filter(html => erIkkeNil(html)),
            //vent slik at endringer i innhold får tid til å bli tegnet ut
            delay(10), //har forsøkt med angulars afterNextRender() men dom er da fortsatt ikke oppdatert
            //finn alle intern-lenker, men vent til angular har oppdatert gui slik at innhold er tegnet ut
            map(html => Array.from(this.el.nativeElement.querySelectorAll('a'))));
    }

    /** Viser panel med gui for lenke-info når bruker har mus over en a-tag */
    private visLenkepanelVedAktivLenke() {
        this.lenker
          .pipe(
            //for hver lenk - lytt til mus inn/ut hendelser
            switchMap(lenker => merge(...lenker.map(lenke => merge(
              //mus inn -> vi viser lenkepanel (etter en liten forsinkelse)
              fromEvent(lenke, 'mouseenter')
                .pipe(
                  //signaliserer at panel skal vises
                  map(() => lenke),
                  //men avbryter om mus flyttes ut av lenke innen 200ms
                  leverForsinketMedMindreAnnenHendelse(200, lenke, 'mouseleave')),

              //mus ut -> vi skjuler panelet - med mindre mus ble flyttet inn i åpent lenkepanel
              fromEvent<MouseEvent>(lenke, 'mouseleave')
                .pipe(
                  mergeMap(hendelse => {
                      let lenkepanel = this.lenkepanel.panelElement();
                      //dersom lenkepanelet er åpent
                      if (erIkkeNil(lenkepanel))
                          return merge(
                            //gir vi bruker litt tid til å flytte musepeker inn i panelet
                            of(hendelse).pipe(leverForsinketMedMindreAnnenHendelse(200, lenkepanel, 'mouseenter')), //avbryter lukk-hendelsen fra lenke-element dersom vi entrer lenkepanelet
                            //lukker fortsatt lenkepanel når mus beveger seg ut av dette, med mindre musen flyttes inn i lenke-elementet igjen..
                            fromEvent(lenkepanel, 'mouseleave').pipe(leverForsinketMedMindreAnnenHendelse(200, lenke, 'mouseenter')));
                      //dersom lenkepanelet ikke er åpent gjør vi ingen videre mapping -> lenkepanel lukkes
                      return of(hendelse);
                  }),
                  //ikke lukk panelet dersom bruker har aktivert forhåndsvisning - da må panelet lukkes ved klikk på 'X' eller esc
                  filter(e => !this.lenkepanel.forhåndsvisningErAktivert()),
                  map(e => null)) //signaliserer at panel skal lukkes ved å levere null
            )))),
            distinctUntilChanged() //ignorer duplikate leveranser - for å unngå unødvendig instansiering av panel
          )
          .subscribe(lenkeElement => {
              if (erNil(lenkeElement)) return this.lenkepanel.lukk();
              this.lenkepanel.visUtenRedigerStøtte(this.lenkeinformasjon(lenkeElement), lenkeElement);
          });
    }

    private lenkeinformasjon(lenkeElement: HTMLAnchorElement): Lenkeinformasjon {
        return {
            adresse: lenkeElement.href,
            lenketekst: lenkeElement.text,
            attributter: {
                dataUuid: lesUuidFraInternlenke(lenkeElement) || undefined,
                dataId: lesIdFraInternlenke(lenkeElement) || undefined,
                dataType: lesTypeFraInternlenke(lenkeElement) || undefined
            }
        };
    }

    private håndterKlikkPåLenke() {
        this.lenker.pipe(
          //for hver lenk - lytt til klikk
          switchMap(lenker => merge(...lenker.map(lenke =>
            fromEvent(lenke, 'click').pipe(
              tap(e => e.preventDefault()), // ikke la klikk boble opp - vi håndterer visning
              map(e => lenke))))), //map til lenkeelementet klikket ble utført på
        )
          .subscribe(lenkeElement => this.vedKlikkPåLenke(lenkeElement));
    }

    private vedKlikkPåLenke(lenkeElement: HTMLAnchorElement) {
        const lenkeinformasjon = this.lenkeinformasjon(lenkeElement);
        //vi støtter her også internlenker laget med ckeditor
        if (erInternLenke(lenkeinformasjon.attributter, true))
            this.vedKlikkPåInternlenke(lenkeinformasjon.attributter, lenkeinformasjon.adresse);
        else
            this.vedKlikkPåEksternlenke(lenkeinformasjon.adresse);
    }

    private vedKlikkPåInternlenke(lenke: InternlenkeAttributter, href: HttpsUrl | null) {
        debug('vedKlikkPåInternlenke', lenke)
        switch (lenke.dataType) {
            case InternlenkeMålEntitetType.opplastetDokumentVersjon:
                this.navigasjon.gåTilSakEllerArkivKnyttetTilDokumentVersjon(lenke.dataId!);
                return;
            case InternlenkeMålEntitetType.sak:
                this.navigasjon.gåTilSak(lenke.dataId!);
                return;
            case InternlenkeMålEntitetType.oppgave:
                this.navigasjon.gåTilOppgave(lenke.dataId!);
                return;
            case InternlenkeMålEntitetType.beslutning:
                this.navigasjon.gåTilBeslutning(lenke.dataId!);
                return;
            default:
                caseForEnumManglerIkkeKastException(lenke.dataType);
                //dersom ikke-kjent dataType er angitt faller vi tilbake på å benytte href
                this.vedKlikkPåEksternlenke(href);
        }
    }

    private vedKlikkPåEksternlenke(adresse: HttpsUrl | null) {
        debug('vedKlikkPåEksternlenke', adresse);
        if (erIkkeNil(adresse)) åpneINyttvindu(adresse);
    }
}
